import { Controller } from '@hotwired/stimulus'

// Sliding puzzle animation controller
// Creates a subtle, continuous sliding effect where tiles move into empty spaces
export default class extends Controller {
  static targets = ['leftGrid', 'rightGrid']

  connect() {
    console.log('Sliding puzzle controller connected')

    this.leftEmptyIndex = 3 // Starting position of empty slot (index 3 in left grid)
    this.rightEmptyIndex = 2 // Starting position of empty slot (index 2 in right grid)

    // Start the animation loop after a short delay
    setTimeout(() => this.startAnimation(), 1000)
  }

  disconnect() {
    if (this.animationTimer) {
      clearInterval(this.animationTimer)
    }
  }

  startAnimation() {
    console.log('Starting animation')

    // Initial animations
    this.slideRandomTile('left')

    setTimeout(() => this.slideRandomTile('right'), 1500)

    // Animate every 3 seconds, alternating grids
    let useLeft = true
    this.animationTimer = setInterval(() => {
      if (useLeft) {
        this.slideRandomTile('left')
      } else {
        this.slideRandomTile('right')
      }
      useLeft = !useLeft
    }, 3000)
  }

  slideRandomTile(side) {
    console.log('slideRandomTile:', side)

    const grid = side === 'left' ? this.leftGridTarget : this.rightGridTarget
    const emptyIndex = side === 'left' ? this.leftEmptyIndex : this.rightEmptyIndex

    if (!grid) {
      console.log('Grid not found for side:', side)
      return
    }

    // Find adjacent indices to the empty space (in a 3x3 grid)
    const adjacentIndices = this.getAdjacentIndices(emptyIndex)
    console.log('Adjacent indices for', emptyIndex, ':', adjacentIndices)

    if (adjacentIndices.length === 0) return

    // Pick a random adjacent tile
    const tileIndex = adjacentIndices[Math.floor(Math.random() * adjacentIndices.length)]
    console.log('Moving tile', tileIndex, 'to empty', emptyIndex)

    // Perform the swap animation
    this.animateSwap(grid, tileIndex, emptyIndex, side)
  }

  getAdjacentIndices(emptyIndex) {
    const row = Math.floor(emptyIndex / 3)
    const col = emptyIndex % 3
    const adjacent = []

    // Up
    if (row > 0) adjacent.push(emptyIndex - 3)
    // Down
    if (row < 2) adjacent.push(emptyIndex + 3)
    // Left
    if (col > 0) adjacent.push(emptyIndex - 1)
    // Right
    if (col < 2) adjacent.push(emptyIndex + 1)

    return adjacent
  }

  animateSwap(grid, tileIndex, emptyIndex, side) {
    const tiles = Array.from(grid.children)
    const tileEl = tiles[tileIndex]
    const emptyEl = tiles[emptyIndex]

    if (!tileEl || !emptyEl) {
      console.log('Missing elements:', { tileEl, emptyEl })
      return
    }

    // Calculate direction of movement
    const tileRow = Math.floor(tileIndex / 3)
    const tileCol = tileIndex % 3
    const emptyRow = Math.floor(emptyIndex / 3)
    const emptyCol = emptyIndex % 3

    const rowDiff = emptyRow - tileRow
    const colDiff = emptyCol - tileCol

    // Get tile dimensions including gap (gap-3 = 12px)
    const tileWidth = tileEl.offsetWidth
    const tileHeight = tileEl.offsetHeight
    const gap = 12

    const translateX = colDiff * (tileWidth + gap)
    const translateY = rowDiff * (tileHeight + gap)

    console.log('Animating:', { translateX, translateY, tileWidth, tileHeight })

    // Add relative positioning for transform to work
    tileEl.style.position = 'relative'
    tileEl.style.zIndex = '10'

    // Apply the slide animation
    tileEl.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    tileEl.style.transform = `translate(${translateX}px, ${translateY}px)`

    // After animation completes, swap DOM positions
    setTimeout(() => {
      // Reset styles
      tileEl.style.transition = 'none'
      tileEl.style.transform = ''
      tileEl.style.position = ''
      tileEl.style.zIndex = ''

      // Swap the elements in the DOM using a placeholder
      const placeholder = document.createElement('div')
      grid.insertBefore(placeholder, tileEl)
      grid.insertBefore(tileEl, emptyEl)
      grid.insertBefore(emptyEl, placeholder)
      placeholder.remove()

      // Update empty index
      if (side === 'left') {
        this.leftEmptyIndex = tileIndex
      } else {
        this.rightEmptyIndex = tileIndex
      }

      console.log('Swap complete, new empty index:', tileIndex)

      // Re-enable transitions after a tick
      requestAnimationFrame(() => {
        tileEl.style.transition = ''
      })
    }, 600)
  }
}
